import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";
import PageHeader from "./PageHeader";
import WorkTimeWidget from "./WorkTimeWidget";

export default function Dashboard({ companies: propCompanies = [], users = [], currentUserId, userRole, isDarkMode = false, fetchCompanies }) {
  const navigate = useNavigate();
  
  const [allCompanies, setAllCompanies] = useState([]);
  const [allUpsells, setAllUpsells] = useState([]);
  const [activityGraph, setActivityGraph] = useState([]); // [{userId, date, count}] za posledních 14 dní
  const [isLoading, setIsLoading] = useState(true);
  const [cacheAge, setCacheAge] = useState(null);
  
  // Nový systém nastavení widgetů
  const [widgetSettings, setWidgetSettings] = useState(() => {
    const saved = localStorage.getItem('dashboardWidgetSettings');
    return saved ? JSON.parse(saved) : {
      totalCompanies: { visibleFor: 'all', showAllUsersData: false },
      myCompletedThisMonth: { visibleFor: 'all', showAllUsersData: false },
      completedThisMonth: { visibleFor: 'all', showAllUsersData: false },
      myBeforeSignature: { visibleFor: 'all', showAllUsersData: false },
      beforeSignature: { visibleFor: 'all', showAllUsersData: false },
      myPoFoRatio: { visibleFor: 'all', showAllUsersData: false },
      poFoRatio: { visibleFor: 'all', showAllUsersData: false },
      operatorRatio: { visibleFor: 'all', showAllUsersData: false },
      activitiesGraph: { visibleFor: 'all', showAllUsersData: true },
      plannedActivitiesGraph: { visibleFor: 'all', showAllUsersData: true },
      closedDealsTable: { visibleFor: 'all', showAllUsersData: true },
      pipelineGraph: { visibleFor: 'all', showAllUsersData: true },
      workTimeStats: { visibleFor: 'all', showAllUsersData: false }
    };
  });
  
  // Admin filtr - nezávislé přepínání pro admina (nezávislé na widgetSettings)
  const [adminShowAll, setAdminShowAll] = useState(() => {
    const saved = localStorage.getItem('dashboardAdminShowAll');
    return saved ? JSON.parse(saved) : {};
  });
  
  // Modal pro nastavení viditelnosti
  const [settingsModal, setSettingsModal] = useState(null); // { containerKey, containerName }
  
  // Modaly pro detail rozpracovanosti
  const [pipelineModal, setPipelineModal] = useState(null); // { userName, forecast, companies: [] }
  const [showOperatorBreakdown, setShowOperatorBreakdown] = useState(false);
  const [showPoFoBreakdown, setShowPoFoBreakdown] = useState(false);
  const [editingForecast, setEditingForecast] = useState(null); // { companyId, newForecast }
  
  // Modaly pro zobchodované a před podpisem
  const [completedModal, setCompletedModal] = useState(null); // { companies: [] }
  const [myCompletedModal, setMyCompletedModal] = useState(null); // { companies: [] } - osobní modal
  const [beforeSignatureModal, setBeforeSignatureModal] = useState(null); // { companies: [] }
  const [myBeforeSignatureModal, setMyBeforeSignatureModal] = useState(null); // { companies: [] } - osobní modal
  const [signedDealsModal, setSignedDealsModal] = useState(null); // { companies: [] } - podepsané obchody
  
  // Modal pro detail končících smluv
  const [expirationModal, setExpirationModal] = useState(null); // { userName, monthLabel, companies: [] }
  
  // Layout pro admin (localStorage)
  const [layoutEditMode, setLayoutEditMode] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedOver, setDraggedOver] = useState(null);
  
  const [layout, setLayout] = useState(() => {
    const saved = localStorage.getItem('dashboardLayout');
    return saved ? JSON.parse(saved) : {
      totalCompanies: { col: 0, row: 0, width: 2, height: 1 },
      myCompletedThisMonth: { col: 2, row: 0, width: 2, height: 1 }, // Osobní widget
      completedThisMonth: { col: 4, row: 0, width: 2, height: 1 }, // Firemní widget
      myBeforeSignature: { col: 0, row: 1, width: 2, height: 1 }, // Osobní widget
      beforeSignature: { col: 2, row: 1, width: 2, height: 1 }, // Firemní widget
      myPoFoRatio: { col: 4, row: 1, width: 1, height: 1 }, // Osobní PO/FO
      poFoRatio: { col: 5, row: 1, width: 1, height: 1 }, // Firemní PO/FO
      operatorRatio: { col: 0, row: 2, width: 1, height: 1 }, // Operátoři O2/T-Mobile
      activitiesGraph: { col: 0, row: 3, width: 8, height: 2 },
      plannedActivitiesGraph: { col: 0, row: 5, width: 8, height: 2 },
      closedDealsTable: { col: 0, row: 7, width: 8, height: 2 },
      pipelineGraph: { col: 0, row: 9, width: 8, height: 2 }
    };
  });
  
  // Drag & drop handlery
  const handleDragStart = (e, itemKey) => {
    if (!layoutEditMode) return;
    setDraggedItem(itemKey);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleDragOver = (e, itemKey) => {
    if (!layoutEditMode) return;
    e.preventDefault();
    setDraggedOver(itemKey);
  };
  
  const handleDragEnd = () => {
    setDraggedItem(null);
    setDraggedOver(null);
  };
  
  const handleDrop = (e, targetKey) => {
    if (!layoutEditMode || !draggedItem || draggedItem === targetKey) {
      setDraggedItem(null);
      setDraggedOver(null);
      return;
    }
    
    e.preventDefault();
    
    // Prohod pozice draggedItem a targetKey v gridu
    setLayout(prev => {
      const newLayout = { ...prev };
      const temp = { ...newLayout[draggedItem] };
      newLayout[draggedItem] = { ...newLayout[targetKey] };
      newLayout[targetKey] = temp;
      return newLayout;
    });
    
    setDraggedItem(null);
    setDraggedOver(null);
  };
  
  // Uložit nastavení widgetů do localStorage
  useEffect(() => {
    localStorage.setItem('dashboardWidgetSettings', JSON.stringify(widgetSettings));
  }, [widgetSettings]);
  
  // Uložit admin filtr do localStorage
  useEffect(() => {
    localStorage.setItem('dashboardAdminShowAll', JSON.stringify(adminShowAll));
  }, [adminShowAll]);
  
  // Uložit layout do localStorage
  useEffect(() => {
    localStorage.setItem('dashboardLayout', JSON.stringify(layout));
  }, [layout]);
  
  // Načti VŠECHNY firmy a upselly najednou přes cached endpoint
  useEffect(() => {
    const fetchAllCompaniesAndUpsells = async () => {
      setIsLoading(true);
      try {
        const res = await fetch(`${API_URL}/dashboard/data?userId=${currentUserId}`);
        if (res.ok) {
          const data = await res.json();
          setAllCompanies(data.companies || []);
          setAllUpsells(data.upsells || []);
          setActivityGraph(data.activityGraph || []);
          setCacheAge({ cachedAt: data.cachedAt, fromCache: data.fromCache });
        } else {
          setAllCompanies([]);
          setAllUpsells([]);
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        setAllCompanies([]);
        setAllUpsells([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (currentUserId) {
      fetchAllCompaniesAndUpsells();
    }
  }, [currentUserId]);
  
  // Filtruj firmy a upselly aktuálního uživatele podle createdBy
  const myCompanies = allCompanies.filter(c => c.createdBy === currentUserId || c.createdBy?._id === currentUserId);
  const myUpsells = allUpsells.filter(u => u.createdBy === currentUserId || u.createdBy?._id === currentUserId);
  
  console.log('Dashboard data:', {
    allCompaniesCount: allCompanies.length,
    allUpsellsCount: allUpsells.length,
    currentUserId,
    myCompaniesCount: myCompanies.length,
    myUpsellsCount: myUpsells.length,
    allUsersCount: users.length,
    isLoading
  });

  // Pomocná funkce - kontrola aktuálního měsíce
  const isCurrentMonth = (date) => {
    if (!date) return false;
    const now = new Date();
    const checkDate = new Date(date);
    return checkDate.getMonth() === now.getMonth() && checkDate.getFullYear() === now.getFullYear();
  };

  // Pomocná funkce - kontrola minulého měsíce
  const isLastMonth = (date) => {
    if (!date) return false;
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const checkDate = new Date(date);
    return checkDate.getMonth() === lastMonth.getMonth() && checkDate.getFullYear() === lastMonth.getFullYear();
  };

  // Pomocná funkce pro filtrování dat podle nastavení
  const getFilteredCompanies = (filterKey) => {
    const settings = widgetSettings[filterKey];
    
    // Admin vždy vidí všechna data
    // Pravé tlačítko na ozubeném kolečku otevře nastavení pro běžné uživatele
    if (userRole === 'admin') {
      return allCompanies; // Admin vždy vidí všechny uživatele
    }
    
    // Běžný uživatel - vidí data podle nastavení showAllUsersData z widgetSettings
    // Toto nastavení ovládá admin v modalu (ozubené kolečko → modal → toggle "Zobrazení dat")
    return settings?.showAllUsersData ? allCompanies : myCompanies;
  };
  
  // Pomocná funkce pro zjištění zda widget je viditelný
  const isWidgetVisible = (filterKey) => {
    const settings = widgetSettings[filterKey];
    if (!settings) return true; // Pokud není nastavení, zobrazit
    
    // Admin vidí vždy všechny widgety
    if (userRole === 'admin') return true;
    
    // Běžný uživatel vidí jen pokud je visibleFor 'all'
    return settings.visibleFor === 'all';
  };

  // Statistiky - respektují filtry
  const totalCompaniesData = getFilteredCompanies('totalCompanies');
  const totalCompanies = totalCompaniesData.length;
  
  // Zobchodováno tento měsíc - ROZDĚLENO na osobní a firemní
  // Osobní statistiky - vždy jen moje firmy + moje upselly
  const myCompletedThisMonthCompanies = myCompanies.filter(c => {
    const forecast = parseInt(c.forecast);
    if (forecast !== 100) return false;
    return isCurrentMonth(c.closedDate) || isCurrentMonth(c.zobchodovano);
  });
  const myCompletedThisMonthUpsells = myUpsells.filter(u => {
    const forecast = parseInt(u.forecast);
    if (forecast !== 100) return false;
    return isCurrentMonth(u.closedDate);
  });
  const myCompletedThisMonth = myCompletedThisMonthCompanies.length + myCompletedThisMonthUpsells.length;
  const myCompletedThisMonthBMSL = 
    myCompletedThisMonthCompanies.reduce((sum, c) => sum + (parseFloat(c.bmsl) || 0), 0) +
    myCompletedThisMonthUpsells.reduce((sum, u) => sum + (parseFloat(u.bmsl) || 0), 0);

  // Firemní statistiky - podle filtru (admin může zobrazit/skrýt)
  const completedData = getFilteredCompanies('completedThisMonth');
  const completedThisMonthCompanies = completedData.filter(c => {
    const forecast = parseInt(c.forecast);
    if (forecast !== 100) return false;
    return isCurrentMonth(c.closedDate) || isCurrentMonth(c.zobchodovano);
  });
  // Filtruj upselly podle toho, jestli admin chce vidět všechny nebo jen své
  // Pro admina vždy všechny upselly (konzistentně s companies)
  const completedThisMonthUpsells = (userRole === 'admin' ? allUpsells : (widgetSettings.completedThisMonth?.showAllUsersData ? allUpsells : myUpsells)).filter(u => {
    const forecast = parseInt(u.forecast);
    if (forecast !== 100) return false;
    return isCurrentMonth(u.closedDate);
  });
  const completedThisMonth = completedThisMonthCompanies.length + completedThisMonthUpsells.length;
  const completedThisMonthBMSL = 
    completedThisMonthCompanies.reduce((sum, c) => sum + (parseFloat(c.bmsl) || 0), 0) +
    completedThisMonthUpsells.reduce((sum, u) => sum + (parseFloat(u.bmsl) || 0), 0);

  // Před podpisem - ROZDĚLENO na osobní a firemní
  // Osobní statistiky - vždy jen moje firmy + moje upselly
  const myBeforeSignatureCompanies = myCompanies.filter(c => {
    const forecast = parseInt(c.forecast);
    return forecast >= 75 && forecast <= 99;
  });
  const myBeforeSignatureUpsells = myUpsells.filter(u => {
    const forecast = parseInt(u.forecast);
    return forecast >= 75 && forecast <= 99;
  });
  const myBeforeSignature = myBeforeSignatureCompanies.length + myBeforeSignatureUpsells.length;
  const myBeforeSignatureBMSL = 
    myBeforeSignatureCompanies.reduce((sum, c) => sum + (parseFloat(c.bmsl) || 0), 0) +
    myBeforeSignatureUpsells.reduce((sum, u) => sum + (parseFloat(u.bmsl) || 0), 0);

  // Firemní statistiky - podle filtru (admin může zobrazit/skrýt)
  const beforeSignatureData = getFilteredCompanies('beforeSignature');
  const beforeSignatureCompanies = beforeSignatureData.filter(c => {
    const forecast = parseInt(c.forecast);
    return forecast >= 75 && forecast <= 99;
  });
  // Pro admina vždy všechny upselly (konzistentně s companies)
  const beforeSignatureUpsells = (userRole === 'admin' ? allUpsells : (widgetSettings.beforeSignature?.showAllUsersData ? allUpsells : myUpsells)).filter(u => {
    const forecast = parseInt(u.forecast);
    return forecast >= 75 && forecast <= 99;
  });
  const beforeSignature = beforeSignatureCompanies.length + beforeSignatureUpsells.length;
  const beforeSignatureBMSL = 
    beforeSignatureCompanies.reduce((sum, c) => sum + (parseFloat(c.bmsl) || 0), 0) +
    beforeSignatureUpsells.reduce((sum, u) => sum + (parseFloat(u.bmsl) || 0), 0);

  // PO/FO poměr - ROZDĚLENO na osobní a firemní
  // Osobní statistiky - vždy jen moje firmy
  const myPoCount = myCompanies.filter(c => c.personType === 'Právnická osoba').length;
  const myFoCount = myCompanies.filter(c => c.personType === 'Fyzická osoba').length;
  const myTotalTyped = myPoCount + myFoCount;
  const myPoPercent = myTotalTyped > 0 ? Math.round((myPoCount / myTotalTyped) * 100) : 0;
  const myFoPercent = myTotalTyped > 0 ? Math.round((myFoCount / myTotalTyped) * 100) : 0;

  // Firemní statistiky - podle filtru (admin může zobrazit/skrýt)
  const poFoData = getFilteredCompanies('poFoRatio');
  const poCount = poFoData.filter(c => c.personType === 'Právnická osoba').length;
  const foCount = poFoData.filter(c => c.personType === 'Fyzická osoba').length;
  const totalTyped = poCount + foCount;
  const poPercent = totalTyped > 0 ? Math.round((poCount / totalTyped) * 100) : 0;
  const foPercent = totalTyped > 0 ? Math.round((foCount / totalTyped) * 100) : 0;

  // Rozpad PO/FO podle obchodníků
  const poFoByUser = (() => {
    const map = {};
    poFoData.forEach(c => {
      if (c.personType !== 'Právnická osoba' && c.personType !== 'Fyzická osoba') return;
      let userName = 'Neznámý';
      if (c.assignedTo) {
        const u = users.find(u => u._id === (c.assignedTo?._id || c.assignedTo));
        if (u) userName = u.name || u.username || 'Neznámý';
      } else if (c.createdBy) {
        const u = users.find(u => u._id === (c.createdBy?._id || c.createdBy));
        if (u) userName = u.name || u.username || 'Neznámý';
      }
      if (!map[userName]) map[userName] = { po: 0, fo: 0 };
      if (c.personType === 'Právnická osoba') map[userName].po++;
      else map[userName].fo++;
    });
    return Object.entries(map)
      .map(([name, counts]) => ({
        name,
        po: counts.po,
        fo: counts.fo,
        total: counts.po + counts.fo,
        poPct: Math.round((counts.po / (counts.po + counts.fo)) * 100),
        foPct: Math.round((counts.fo / (counts.po + counts.fo)) * 100),
      }))
      .sort((a, b) => b.total - a.total);
  })();

  // Poměr operátorů (O2 / T-Mobile) - pouze pro můj poměr
  const operatorData = getFilteredCompanies('operatorRatio');
  const o2Count = operatorData.filter(c => c.operator === 'O2').length;
  const tmobileCount = operatorData.filter(c => c.operator === 'T-Mobile').length;
  const totalOperators = o2Count + tmobileCount;
  const o2Percent = totalOperators > 0 ? Math.round((o2Count / totalOperators) * 100) : 0;
  const tmobilePercent = totalOperators > 0 ? Math.round((tmobileCount / totalOperators) * 100) : 0;

  // Rozpad poměru operátorů podle obchodníků
  const operatorByUser = (() => {
    const map = {};
    operatorData.forEach(c => {
      if (c.operator !== 'O2' && c.operator !== 'T-Mobile') return;
      // zjisti jméno obchodníka
      let userName = 'Neznámý';
      if (c.assignedTo) {
        const u = users.find(u => u._id === (c.assignedTo?._id || c.assignedTo));
        if (u) userName = u.name || u.username || 'Neznámý';
      } else if (c.createdBy) {
        const u = users.find(u => u._id === (c.createdBy?._id || c.createdBy));
        if (u) userName = u.name || u.username || 'Neznámý';
      }
      if (!map[userName]) map[userName] = { o2: 0, tmobile: 0 };
      if (c.operator === 'O2') map[userName].o2++;
      else map[userName].tmobile++;
    });
    return Object.entries(map)
      .map(([name, counts]) => ({
        name,
        o2: counts.o2,
        tmobile: counts.tmobile,
        total: counts.o2 + counts.tmobile,
        o2Pct: Math.round((counts.o2 / (counts.o2 + counts.tmobile)) * 100),
        tmobilePct: Math.round((counts.tmobile / (counts.o2 + counts.tmobile)) * 100),
      }))
      .sort((a, b) => b.total - a.total);
  })();

  // Graf aktivit za poslední 2 týdny — používá předagregovaná serverová data (activityGraph)
  const getActivitiesData = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Lokální datum string (YYYY-MM-DD) bez UTC posunu — musí souhlasit s timezone v MongoDB agregaci
    const localDateStr = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    const days = [];
    for (let i = 13; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      days.push({
        date,
        dateString: localDateStr(date),
        label: date.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' })
      });
    }
    
    const showAll = userRole === 'admin' || widgetSettings.activitiesGraph?.showAllUsersData;
    const userMap = {};

    activityGraph.forEach(({ userId, date, count }) => {
      const userIdStr = userId?.toString();
      if (!showAll && userIdStr !== currentUserId) return;

      const dayIndex = days.findIndex(d => d.dateString === date);
      if (dayIndex === -1) return;

      if (!userMap[userIdStr]) {
        const user = users.find(u => u._id === userIdStr);
        userMap[userIdStr] = {
          userId: userIdStr,
          name: user?.name || 'Neznámý',
          dailyCounts: new Array(14).fill(0),
          total: 0
        };
      }
      userMap[userIdStr].dailyCounts[dayIndex] += count;
      userMap[userIdStr].total += count;
    });
    
    return {
      days,
      users: Object.values(userMap).filter(u => u.total > 0).sort((a, b) => b.total - a.total)
    };
  };
  
  const activitiesData = getActivitiesData();

  // Naplánované aktivity na následující 2 týdny
  const getPlannedActivitiesData = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Vytvoř pole následujících 14 dní
    const days = [];
    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() + i);
      days.push({
        date: date,
        dateString: date.toISOString().split('T')[0],
        label: date.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' })
      });
    }
    
    const userActivities = {};
    
    // Projdi firmy podle filtru
    const filteredCompanies = getFilteredCompanies('plannedActivitiesGraph');
    filteredCompanies.forEach(company => {
      // Kontroluj nextActionDate (datum další plánované akce)
      if (!company.nextActionDate) return;
      
      const nextActionDate = new Date(company.nextActionDate);
      if (isNaN(nextActionDate.getTime())) return; // Přeskoč nevalidní data
      
      nextActionDate.setHours(0, 0, 0, 0);
      
      // Kontrola, zda aktivita je v budoucnosti nebo dnes
      if (nextActionDate < today) return;
      
      const dateString = nextActionDate.toISOString().split('T')[0];
      
      // Zjisti jestli aktivita je v následujících 14 dnech
      const dayIndex = days.findIndex(d => d.dateString === dateString);
      if (dayIndex === -1) return;
      
      // Zjisti uživatele
      let userId = company.createdBy?._id || company.createdBy;
      if (!userId) return;
      
      // Inicializuj strukturu pro uživatele
      if (!userActivities[userId]) {
        const user = users.find(u => u._id === userId || u._id === userId._id);
        userActivities[userId] = {
          userId,
          name: user?.name || 'Neznámý',
          dailyCounts: new Array(14).fill(0),
          total: 0
        };
      }
      
      userActivities[userId].dailyCounts[dayIndex]++;
      userActivities[userId].total++;
    });
    
    // Převeď na pole a seřaď podle celkového počtu (sestupně)
    return {
      days,
      users: Object.values(userActivities)
        .filter(u => u.total > 0)
        .sort((a, b) => b.total - a.total)
    };
  };
  
  const plannedActivitiesData = getPlannedActivitiesData();

  // Přehled podepsaných obchodů za aktuální měsíc (BMSL styl)
  const getClosedDealsData = () => {
    const userDeals = {};
    
    // Pomocná funkce pro inicializaci záznamu uživatele
    const ensureUser = (userId) => {
      if (!userDeals[userId]) {
        const user = users.find(u => u._id === userId);
        userDeals[userId] = {
          name: user?.name || 'Neznámý',
          count: 0,
          totalBMSL: 0,
          lastMonthBMSL: 0,
          companies: []
        };
      }
    };

    // Projdi firmy podle filtru
    const filteredCompanies = getFilteredCompanies('closedDealsTable');
    filteredCompanies.forEach(company => {
      const forecast = parseInt(company.forecast);
      if (forecast !== 100) return;
      
      const closedDate = company.closedDate || company.zobchodovano;
      if (!closedDate) return;

      const userId = company.createdBy?._id || company.createdBy;
      if (!userId) return;

      const bmsl = parseFloat(company.bmsl) || 0;

      if (isCurrentMonth(closedDate)) {
        ensureUser(userId);
        userDeals[userId].count++;
        userDeals[userId].companies.push(company);
        userDeals[userId].totalBMSL += bmsl;
      } else if (isLastMonth(closedDate)) {
        ensureUser(userId);
        userDeals[userId].lastMonthBMSL += bmsl;
      }
    });
    
    // Projdi také upselly
    const filteredUpsells = userRole === 'admin' ? allUpsells : (widgetSettings.closedDealsTable?.showAllUsersData ? allUpsells : myUpsells);
    filteredUpsells.forEach(upsell => {
      const forecast = parseInt(upsell.forecast);
      if (forecast !== 100) return;
      if (!upsell.closedDate) return;

      const userId = upsell.createdBy?._id || upsell.createdBy;
      if (!userId) return;

      const bmsl = parseFloat(upsell.bmsl) || 0;

      if (isCurrentMonth(upsell.closedDate)) {
        ensureUser(userId);
        userDeals[userId].count++;
        userDeals[userId].companies.push({ ...upsell, name: upsell.companyId?.name || 'Upsell', isUpsell: true });
        userDeals[userId].totalBMSL += bmsl;
      } else if (isLastMonth(upsell.closedDate)) {
        ensureUser(userId);
        userDeals[userId].lastMonthBMSL += bmsl;
      }
    });
    
    // Převeď na pole a seřaď podle celkového BMSL (sestupně)
    return Object.values(userDeals)
      .filter(u => u.count > 0)
      .sort((a, b) => b.totalBMSL - a.totalBMSL);
  };
  
  const closedDealsData = getClosedDealsData();

  // Graf rozpracovanosti podle forecastu (25, 50, 75, 99)
  const getPipelineData = () => {
    const userPipeline = {};
    
    // Projdi firmy podle filtru
    const filteredCompanies = getFilteredCompanies('pipelineGraph');
    filteredCompanies.forEach(company => {
      const forecast = parseInt(company.forecast);
      
      // Pouze forecasty 25, 50, 75, 99 (ne 0 a 100)
      if (![25, 50, 75, 99].includes(forecast)) return;
      
      // Musí mít BMSL
      const bmsl = parseFloat(company.bmsl) || 0;
      if (bmsl === 0) return;
      
      // Zjisti uživatele
      const userId = company.createdBy?._id || company.createdBy;
      if (!userId) return;
      
      // Inicializuj data pro uživatele
      if (!userPipeline[userId]) {
        const user = users.find(u => u._id === userId);
        userPipeline[userId] = {
          userId,
          name: user?.name || 'Neznámý',
          forecast25: { count: 0, bmsl: 0, companies: [] },
          forecast50: { count: 0, bmsl: 0, companies: [] },
          forecast75: { count: 0, bmsl: 0, companies: [] },
          forecast99: { count: 0, bmsl: 0, companies: [] },
          total: 0
        };
      }
      
      // Přiřaď do správné kategorie včetně reference na firmu
      if (forecast === 25) {
        userPipeline[userId].forecast25.count++;
        userPipeline[userId].forecast25.bmsl += bmsl;
        userPipeline[userId].forecast25.companies.push(company);
      } else if (forecast === 50) {
        userPipeline[userId].forecast50.count++;
        userPipeline[userId].forecast50.bmsl += bmsl;
        userPipeline[userId].forecast50.companies.push(company);
      } else if (forecast === 75) {
        userPipeline[userId].forecast75.count++;
        userPipeline[userId].forecast75.bmsl += bmsl;
        userPipeline[userId].forecast75.companies.push(company);
      } else if (forecast === 99) {
        userPipeline[userId].forecast99.count++;
        userPipeline[userId].forecast99.bmsl += bmsl;
        userPipeline[userId].forecast99.companies.push(company);
      }
      
      userPipeline[userId].total += bmsl;
    });
    
    // Projdi také upselly
    // Pro admina vždy všechny upselly (konzistentně s companies)
    const filteredUpsells = userRole === 'admin' ? allUpsells : (widgetSettings.pipelineGraph?.showAllUsersData ? allUpsells : myUpsells);
    filteredUpsells.forEach(upsell => {
      const forecast = parseInt(upsell.forecast);
      
      // Pouze forecasty 25, 50, 75, 99 (ne 0 a 100)
      if (![25, 50, 75, 99].includes(forecast)) return;
      
      // Musí mít BMSL
      const bmsl = parseFloat(upsell.bmsl) || 0;
      if (bmsl === 0) return;
      
      // Zjisti uživatele
      const userId = upsell.createdBy?._id || upsell.createdBy;
      if (!userId) return;
      
      // Inicializuj data pro uživatele
      if (!userPipeline[userId]) {
        const user = users.find(u => u._id === userId);
        userPipeline[userId] = {
          userId,
          name: user?.name || 'Neznámý',
          forecast25: { count: 0, bmsl: 0, companies: [] },
          forecast50: { count: 0, bmsl: 0, companies: [] },
          forecast75: { count: 0, bmsl: 0, companies: [] },
          forecast99: { count: 0, bmsl: 0, companies: [] },
          total: 0
        };
      }
      
      // Přiřaď do správné kategorie s označením upsell
      const upsellData = {
        ...upsell,
        name: upsell.companyId?.name || 'Upsell',
        isUpsell: true
      };
      
      if (forecast === 25) {
        userPipeline[userId].forecast25.count++;
        userPipeline[userId].forecast25.bmsl += bmsl;
        userPipeline[userId].forecast25.companies.push(upsellData);
      } else if (forecast === 50) {
        userPipeline[userId].forecast50.count++;
        userPipeline[userId].forecast50.bmsl += bmsl;
        userPipeline[userId].forecast50.companies.push(upsellData);
      } else if (forecast === 75) {
        userPipeline[userId].forecast75.count++;
        userPipeline[userId].forecast75.bmsl += bmsl;
        userPipeline[userId].forecast75.companies.push(upsellData);
      } else if (forecast === 99) {
        userPipeline[userId].forecast99.count++;
        userPipeline[userId].forecast99.bmsl += bmsl;
        userPipeline[userId].forecast99.companies.push(upsellData);
      }
      
      userPipeline[userId].total += bmsl;
    });
    
    // Převeď na pole a seřaď podle celkového BMSL (sestupně)
    return Object.values(userPipeline)
      .filter(u => u.total > 0)
      .sort((a, b) => b.total - a.total);
  };
  
  const pipelineData = getPipelineData();

  // Nová funkce: Končící smlouvy v následujících 24 měsících
  const getContractExpirations = () => {
    const today = new Date();
    const userExpirations = {};
    
    // Vytvoř pole následujících 24 měsíců
    const months = [];
    for (let i = 0; i < 24; i++) {
      const monthDate = new Date(today.getFullYear(), today.getMonth() + i, 1);
      months.push({
        year: monthDate.getFullYear(),
        month: monthDate.getMonth() + 1,
        label: monthDate.toLocaleDateString('cs-CZ', { month: 'short', year: 'numeric' }),
        fullLabel: monthDate.toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' }),
        key: `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, '0')}`
      });
    }
    
    // Projdi všechny firmy (bez filtru - každý uživatel vidí své firmy)
    allCompanies.forEach(company => {
      // Přeskoč firmy bez rsEndDate
      if (!company.rsEndDate) return;
      
      // Parsuj datum konce RS
      const rsEndDate = new Date(company.rsEndDate);
      if (isNaN(rsEndDate.getTime())) return; // Neplatné datum
      
      // Zjisti, do kterého měsíce končí smlouva
      const endYear = rsEndDate.getFullYear();
      const endMonth = rsEndDate.getMonth() + 1;
      const endKey = `${endYear}-${String(endMonth).padStart(2, '0')}`;
      
      // Kontrola, jestli je v následujících 24 měsících
      const monthIndex = months.findIndex(m => m.key === endKey);
      if (monthIndex === -1) return; // Mimo rozsah
      
      // Zjisti uživatele (createdBy)
      const userId = company.createdBy?._id || company.createdBy;
      if (!userId) return;
      
      // Inicializuj data pro uživatele
      if (!userExpirations[userId]) {
        const user = users.find(u => u._id === userId);
        userExpirations[userId] = {
          userId,
          name: user?.name || 'Neznámý',
          months: months.map(m => ({ ...m, count: 0, companies: [] })),
          total: 0
        };
      }
      
      // Přidej firmu do správného měsíce s informací o poslední aktivitě
      userExpirations[userId].months[monthIndex].count++;
      userExpirations[userId].months[monthIndex].companies.push({
        ...company,
        lastActivityText: 'Bez aktivity',
        lastActivityDate: null
      });
      userExpirations[userId].total++;
    });
    
    // Převeď na pole a seřaď podle celkového počtu (sestupně)
    return {
      months,
      users: Object.values(userExpirations)
        .filter(u => u.total > 0)
        .sort((a, b) => b.total - a.total)
    };
  };
  
  const contractExpirationsData = getContractExpirations();

  // Funkce pro změnu forecastu firmy (admin only)
  const handleForecastChange = async (companyId, newForecast) => {
    try {
      const response = await fetch(`${API_URL}/companies/${companyId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ forecast: newForecast })
      });
      
      if (response.ok) {
        // Aktualizuj lokální data
        setAllCompanies(prev => prev.map(c => 
          c._id === companyId ? { ...c, forecast: newForecast } : c
        ));
        
        // Aktualizuj modal
        if (pipelineModal) {
          setPipelineModal(prev => ({
            ...prev,
            companies: prev.companies.map(c => 
              c._id === companyId ? { ...c, forecast: newForecast } : c
            )
          }));
        }
        
        setEditingForecast(null);
      } else {
        console.error('Chyba při změně forecastu:', response.statusText);
        alert('Nepodařilo se změnit forecast');
      }
    } catch (error) {
      console.error('Chyba při změně forecastu:', error);
      alert('Nepodařilo se změnit forecast');
    }
  };
  
  // Komponenta pro ozubené kolečko - přepíná adminův filtr nebo otevírá nastavení
  const FilterButton = ({ filterKey, containerName }) => {
    // Debug log
    console.log('FilterButton render:', { filterKey, userRole });
    
    // Pouze admin vidí ozubené kolečko
    if (userRole !== 'admin') return null;
    
    return (
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          // Pravé i levé tlačítko otevírá nastavení pro uživatele
          setSettingsModal({ containerKey: filterKey, containerName });
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          // Pravé tlačítko - otevřít modal s nastavením pro uživatele
          setSettingsModal({ containerKey: filterKey, containerName });
        }}
        className="absolute top-2 right-2 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group z-10"
        title="Nastavení viditelnosti pro uživatele (pravé tlačítko)"
      >
        <svg className="w-4 h-4 text-blue-600 dark:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-blue-100 dark:from-gray-800 dark:to-gray-900 py-10">
      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-8 shadow-2xl max-w-md w-full mx-4">
            <div className="flex flex-col items-center gap-4">
              {/* Spinner */}
              <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              
              {/* Text */}
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-700 dark:text-gray-200">Načítám dashboard...</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Připravuji data a statistiky</p>
              </div>
              
              {/* Progress bar */}
              <div className="w-full">
                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
                  <div 
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 animate-pulse"
                    style={{ width: '75%' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="w-[98vw] max-w-[1800px] mx-auto px-4">
        {/* Hlavička */}
        <PageHeader
          title="Dashboard"
          actions={
            userRole === 'admin' && (
              <div className="flex flex-col items-end gap-2">
                <button
                  onClick={() => setLayoutEditMode(!layoutEditMode)}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all shadow-md ${
                    layoutEditMode 
                      ? 'bg-orange-600 hover:bg-orange-700 text-white' 
                      : 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200'
                  }`}
                >
                  {layoutEditMode ? '✓ Hotovo' : '⚙️ Upravit rozložení'}
                </button>
                {layoutEditMode && (
                  <p className="text-xs text-orange-600 dark:text-orange-400 font-medium animate-pulse">
                    💡 Přetáhněte kontejnery myší pro změnu pořadí
                  </p>
                )}
              </div>
            )
          }
        />

        {/* Osobní statistiky - 4 kontejnery */}
        {cacheAge && (
          <div className="flex items-center gap-2 mb-4 text-xs text-gray-400 dark:text-gray-500">
            <span title={`Data naposled aktualizována: ${new Date(cacheAge.cachedAt).toLocaleTimeString('cs-CZ')}`}>
              {cacheAge.fromCache ? '⏱ Data z cache' : '✅ Čerstvá data'} · aktualizováno {new Date(cacheAge.cachedAt).toLocaleTimeString('cs-CZ')} · obnovení každých 10 min
            </span>
            <button
              onClick={async () => {
                await fetch(`${API_URL}/dashboard/invalidate-cache?userId=${currentUserId}`, { method: 'POST' });
                const res = await fetch(`${API_URL}/dashboard/data?userId=${currentUserId}`);
                if (res.ok) {
                  const data = await res.json();
                  setAllCompanies(data.companies || []);
                  setAllUpsells(data.upsells || []);
                  setActivityGraph(data.activityGraph || []);
                  setCacheAge({ cachedAt: data.cachedAt, fromCache: data.fromCache });
                }
              }}
              className="underline hover:text-blue-500 cursor-pointer"
              title="Obnovit data nyní"
            >
              Obnovit
            </button>
          </div>
        )}

        {/* Osobní statistiky - 4 kontejnery */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          
          {/* 1. Celkový počet firem */}
          {isWidgetVisible('totalCompanies') && <div 
            className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border-l-4 border-blue-500 relative ${
              layoutEditMode ? 'cursor-move hover:shadow-2xl transition-shadow' : ''
            } ${draggedOver === 'totalCompanies' ? 'ring-4 ring-blue-400' : ''}`}
            draggable={layoutEditMode}
            onDragStart={(e) => handleDragStart(e, 'totalCompanies')}
            onDragOver={(e) => handleDragOver(e, 'totalCompanies')}
            onDragEnd={handleDragEnd}
            onDrop={(e) => handleDrop(e, 'totalCompanies')}
          >
            {layoutEditMode && (
              <div className="absolute top-2 left-2 text-gray-400 dark:text-gray-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                </svg>
              </div>
            )}
            <FilterButton filterKey="totalCompanies" containerName="Celkem firem v CRM" />
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Celkem firem v CRM</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {totalCompanies}
                </p>
              </div>
              <div className="bg-blue-100 dark:bg-blue-900 rounded-full p-3">
                <svg className="w-8 h-8 text-blue-600 dark:text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            </div>
          </div>}

          {/* 2. Moje zobchodováno tento měsíc - OSOBNÍ */}
          {isWidgetVisible('myCompletedThisMonth') && <div 
            className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border-l-4 border-green-400 relative ${
              layoutEditMode ? 'cursor-move' : 'cursor-pointer'
            } hover:shadow-xl transition-shadow ${draggedOver === 'myCompletedThisMonth' ? 'ring-4 ring-blue-400' : ''}`}
            draggable={layoutEditMode}
            onDragStart={(e) => handleDragStart(e, 'myCompletedThisMonth')}
            onDragOver={(e) => handleDragOver(e, 'myCompletedThisMonth')}
            onDragEnd={handleDragEnd}
            onDrop={(e) => handleDrop(e, 'myCompletedThisMonth')}
            onClick={!layoutEditMode ? () => setMyCompletedModal({ 
              companies: [
                ...myCompletedThisMonthCompanies,
                ...myCompletedThisMonthUpsells.map(u => ({ ...u, name: u.companyId?.name || 'Upsell', isUpsell: true }))
              ]
            }) : undefined}
          >
            {layoutEditMode && (
              <div className="absolute top-2 left-2 text-gray-400 dark:text-gray-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                </svg>
              </div>
            )}
            <FilterButton filterKey="myCompletedThisMonth" containerName="Mé zobchodované firmy tento měsíc" />
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Mé zobchodované firmy tento měsíc</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {myCompletedThisMonth}
                </p>
                <p className="text-lg text-green-600 dark:text-green-400 mt-2 font-bold">
                  {myCompletedThisMonthBMSL.toLocaleString('cs-CZ')} Kč
                </p>
              </div>
              <div className="bg-green-100 dark:bg-green-900 rounded-full p-3">
                <svg className="w-8 h-8 text-green-600 dark:text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>}

          {/* 3. Firma zobchodováno tento měsíc - FIREMNÍ */}
          {isWidgetVisible('completedThisMonth') && <div 
            className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border-l-4 border-green-500 relative ${
              layoutEditMode ? 'cursor-move' : 'cursor-pointer'
            } hover:shadow-xl transition-shadow ${draggedOver === 'completedThisMonth' ? 'ring-4 ring-blue-400' : ''}`}
            draggable={layoutEditMode}
            onDragStart={(e) => handleDragStart(e, 'completedThisMonth')}
            onDragOver={(e) => handleDragOver(e, 'completedThisMonth')}
            onDragEnd={handleDragEnd}
            onDrop={(e) => handleDrop(e, 'completedThisMonth')}
            onClick={!layoutEditMode ? () => setCompletedModal({ 
              companies: [
                ...completedThisMonthCompanies, 
                ...completedThisMonthUpsells.map(u => ({ ...u, name: u.companyId?.name || 'Upsell', isUpsell: true }))
              ] 
            }) : undefined}
          >
            <FilterButton filterKey="completedThisMonth" containerName="Firma celkem zobchodováno" />
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Firma celkem zobchodováno</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {completedThisMonth}
                </p>
                <p className="text-lg text-green-600 dark:text-green-400 mt-2 font-bold">
                  {completedThisMonthBMSL.toLocaleString('cs-CZ')} Kč
                </p>
              </div>
              <div className="bg-green-100 dark:bg-green-900 rounded-full p-3">
                <svg className="w-8 h-8 text-green-600 dark:text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>}

          {/* 4. Moje před podpisem - OSOBNÍ */}
          {isWidgetVisible('myBeforeSignature') && <div 
            className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border-l-4 border-orange-400 relative ${
              layoutEditMode ? 'cursor-move' : 'cursor-pointer'
            } hover:shadow-xl transition-shadow ${draggedOver === 'myBeforeSignature' ? 'ring-4 ring-blue-400' : ''}`}
            draggable={layoutEditMode}
            onDragStart={(e) => handleDragStart(e, 'myBeforeSignature')}
            onDragOver={(e) => handleDragOver(e, 'myBeforeSignature')}
            onDragEnd={handleDragEnd}
            onDrop={(e) => handleDrop(e, 'myBeforeSignature')}
            onClick={!layoutEditMode ? () => setMyBeforeSignatureModal({ 
              companies: [
                ...myBeforeSignatureCompanies,
                ...myBeforeSignatureUpsells.map(u => ({ ...u, name: u.companyId?.name || 'Upsell', isUpsell: true }))
              ]
            }) : undefined}
          >
            {layoutEditMode && (
              <div className="absolute top-2 left-2 text-gray-400 dark:text-gray-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                </svg>
              </div>
            )}
            <FilterButton filterKey="myBeforeSignature" containerName="Moje firmy před podpisem" />
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Moje firmy před podpisem</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {myBeforeSignature}
                </p>
                <p className="text-lg text-orange-600 dark:text-orange-400 mt-2 font-bold">
                  {myBeforeSignatureBMSL.toLocaleString('cs-CZ')} Kč
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Forecast 75-99%</p>
              </div>
              <div className="bg-orange-100 dark:bg-orange-900 rounded-full p-3">
                <svg className="w-8 h-8 text-orange-600 dark:text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
            </div>
          </div>}

          {/* 5. Firma před podpisem - FIREMNÍ */}
          {isWidgetVisible('beforeSignature') && <div 
            className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border-l-4 border-orange-500 relative ${
              layoutEditMode ? 'cursor-move' : 'cursor-pointer'
            } hover:shadow-xl transition-shadow ${draggedOver === 'beforeSignature' ? 'ring-4 ring-blue-400' : ''}`}
            draggable={layoutEditMode}
            onDragStart={(e) => handleDragStart(e, 'beforeSignature')}
            onDragOver={(e) => handleDragOver(e, 'beforeSignature')}
            onDragEnd={handleDragEnd}
            onDrop={(e) => handleDrop(e, 'beforeSignature')}
            onClick={!layoutEditMode ? () => setBeforeSignatureModal({ 
              companies: [
                ...beforeSignatureCompanies,
                ...beforeSignatureUpsells.map(u => ({ ...u, name: u.companyId?.name || 'Upsell', isUpsell: true }))
              ]
            }) : undefined}
          >
            <FilterButton filterKey="beforeSignature" containerName="Firmy celkem před podpisem" />
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Firmy celkem před podpisem</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {beforeSignature}
                </p>
                <p className="text-lg text-orange-600 dark:text-orange-400 mt-2 font-bold">
                  {beforeSignatureBMSL.toLocaleString('cs-CZ')} Kč
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Forecast 75-99%</p>
              </div>
              <div className="bg-orange-100 dark:bg-orange-900 rounded-full p-3">
                <svg className="w-8 h-8 text-orange-600 dark:text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
            </div>
          </div>}

          {/* 6. Moje PO / FO - OSOBNÍ */}
          {isWidgetVisible('myPoFoRatio') && <div 
            className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border-l-4 border-purple-400 relative ${
              layoutEditMode ? 'cursor-move hover:shadow-2xl transition-shadow' : ''
            } ${draggedOver === 'myPoFoRatio' ? 'ring-4 ring-blue-400' : ''}`}
            draggable={layoutEditMode}
            onDragStart={(e) => handleDragStart(e, 'myPoFoRatio')}
            onDragOver={(e) => handleDragOver(e, 'myPoFoRatio')}
            onDragEnd={handleDragEnd}
            onDrop={(e) => handleDrop(e, 'myPoFoRatio')}
          >
            {layoutEditMode && (
              <div className="absolute top-2 left-2 text-gray-400 dark:text-gray-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                </svg>
              </div>
            )}
            <FilterButton filterKey="myPoFoRatio" containerName="Můj poměr PO / FO" />
            <div className="w-full">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">Můj poměr PO / FO</p>
              
              {/* PO */}
              <div className="mb-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-gray-600 dark:text-gray-400">Právnické osoby</span>
                  <span className="text-xs font-semibold text-purple-600 dark:text-purple-400">{myPoCount} ({myPoPercent}%)</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div className="bg-purple-600 h-2 rounded-full transition-all" style={{ width: `${myPoPercent}%` }}></div>
                </div>
              </div>
              
              {/* FO */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-gray-600 dark:text-gray-400">Fyzické osoby</span>
                  <span className="text-xs font-semibold text-purple-400 dark:text-purple-300">{myFoCount} ({myFoPercent}%)</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div className="bg-purple-400 h-2 rounded-full transition-all" style={{ width: `${myFoPercent}%` }}></div>
                </div>
              </div>
            </div>
          </div>}

          {/* 7. Firma PO / FO - FIREMNÍ */}
          {isWidgetVisible('poFoRatio') && <div 
            className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border-l-4 border-purple-500 relative ${
              layoutEditMode ? 'cursor-move hover:shadow-2xl transition-shadow' : ''
            } ${draggedOver === 'poFoRatio' ? 'ring-4 ring-blue-400' : ''}`}
            draggable={layoutEditMode}
            onDragStart={(e) => handleDragStart(e, 'poFoRatio')}
            onDragOver={(e) => handleDragOver(e, 'poFoRatio')}
            onDragEnd={handleDragEnd}
            onDrop={(e) => handleDrop(e, 'poFoRatio')}
          >
            {layoutEditMode && (
              <div className="absolute top-2 left-2 text-gray-400 dark:text-gray-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                </svg>
              </div>
            )}
            <FilterButton filterKey="poFoRatio" containerName="Firemní poměr PO / FO" />
            <div className="w-full">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Firemní poměr PO / FO</p>
                {poFoByUser.length > 0 && (
                  <button
                    onClick={() => setShowPoFoBreakdown(true)}
                    className="text-xs text-purple-600 dark:text-purple-400 hover:underline font-medium"
                  >
                    Rozpad podle obchodníků →
                  </button>
                )}
              </div>
              
              {/* PO */}
              <div className="mb-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-gray-600 dark:text-gray-400">Právnické osoby</span>
                  <span className="text-xs font-semibold text-purple-600 dark:text-purple-400">{poCount} ({poPercent}%)</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div className="bg-purple-600 h-2 rounded-full transition-all" style={{ width: `${poPercent}%` }}></div>
                </div>
              </div>
              
              {/* FO */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-gray-600 dark:text-gray-400">Fyzické osoby</span>
                  <span className="text-xs font-semibold text-purple-400 dark:text-purple-300">{foCount} ({foPercent}%)</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div className="bg-purple-400 h-2 rounded-full transition-all" style={{ width: `${foPercent}%` }}></div>
                </div>
              </div>
            </div>
          </div>}

          {/* 8. Poměr operátorů O2 / T-Mobile */}
          {isWidgetVisible('operatorRatio') && (
            <div 
              className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border-l-4 border-cyan-500 relative ${
                layoutEditMode ? 'cursor-move hover:shadow-2xl transition-shadow' : ''
              } ${draggedOver === 'operatorRatio' ? 'ring-4 ring-blue-400' : ''}`}
              draggable={layoutEditMode}
              onDragStart={(e) => handleDragStart(e, 'operatorRatio')}
              onDragOver={(e) => handleDragOver(e, 'operatorRatio')}
              onDragEnd={handleDragEnd}
              onDrop={(e) => handleDrop(e, 'operatorRatio')}
            >
              {layoutEditMode && (
                <div className="absolute top-2 left-2 text-gray-400 dark:text-gray-500">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                  </svg>
                </div>
              )}
              <FilterButton filterKey="operatorRatio" containerName="Poměr operátorů O2 / T-Mobile" />
              <div className="w-full">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Poměr operátorů</p>
                  {operatorByUser.length > 0 && (
                    <button
                      onClick={() => setShowOperatorBreakdown(true)}
                      className="text-xs text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                    >
                      Rozpad podle obchodníků →
                    </button>
                  )}
                </div>
                
                {/* O2 */}
                <div className="mb-3">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-gray-600 dark:text-gray-400">O2</span>
                    <span className="text-xs font-semibold text-cyan-600 dark:text-cyan-400">{o2Count} ({o2Percent}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div className="bg-cyan-600 h-2 rounded-full transition-all" style={{ width: `${o2Percent}%` }}></div>
                  </div>
                </div>
                
                {/* T-Mobile */}
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-gray-600 dark:text-gray-400">T-Mobile</span>
                    <span className="text-xs font-semibold text-pink-600 dark:text-pink-400">{tmobileCount} ({tmobilePercent}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div className="bg-pink-600 h-2 rounded-full transition-all" style={{ width: `${tmobilePercent}%` }}></div>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Přehled podepsaných obchodů za aktuální měsíc */}
        {closedDealsData.length > 0 && isWidgetVisible('closedDealsTable') && (
          <div 
            className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8 relative ${
              layoutEditMode ? 'cursor-move hover:shadow-2xl transition-shadow' : ''
            } ${draggedOver === 'closedDealsTable' ? 'ring-4 ring-blue-400' : ''}`}
            draggable={layoutEditMode}
            onDragStart={(e) => handleDragStart(e, 'closedDealsTable')}
            onDragOver={(e) => handleDragOver(e, 'closedDealsTable')}
            onDragEnd={handleDragEnd}
            onDrop={(e) => handleDrop(e, 'closedDealsTable')}
          >
            <div className="absolute top-4 right-4">
              <FilterButton filterKey="closedDealsTable" containerName="Podepsané obchody" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
              Podepsané obchody - {new Date().toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' })}
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Obchodník
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Počet obchodů
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Podepsáno ({new Date().toLocaleDateString('cs-CZ', { month: 'long' })})
                    </th>
                    <th className="w-[160px]"></th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Průměr/obchod
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {closedDealsData.map((user, index) => {
                    const avgBMSL = user.count > 0 ? (user.totalBMSL / user.count) : 0;
                    const maxBMSL = closedDealsData[0].totalBMSL;
                    const percentage = maxBMSL > 0 ? (user.totalBMSL / maxBMSL) * 100 : 0;
                    
                    return (
                      <tr 
                        key={index} 
                        className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                        onClick={() => setSignedDealsModal({ userName: user.name, companies: user.companies })}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {user.name}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="inline-flex items-center justify-center px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full text-sm font-semibold">
                            {user.count}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="font-bold text-gray-900 dark:text-white tabular-nums">
                            {user.totalBMSL.toLocaleString('cs-CZ')} Kč
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="w-full min-w-[80px] max-w-[160px]">
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                              <div 
                                className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-gray-600 dark:text-gray-400">
                            {avgBMSL.toLocaleString('cs-CZ', { maximumFractionDigits: 0 })} Kč
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 dark:bg-gray-700 font-bold">
                    <td className="py-3 px-4 text-gray-900 dark:text-white">
                      CELKEM
                    </td>
                    <td className="py-3 px-4 text-center text-gray-900 dark:text-white">
                      {closedDealsData.reduce((sum, u) => sum + u.count, 0)}
                    </td>
                    <td className="py-3 px-4 text-right text-gray-900 dark:text-white">
                      {closedDealsData.reduce((sum, u) => sum + u.totalBMSL, 0).toLocaleString('cs-CZ')} Kč
                    </td>
                    <td className="py-3 px-4"></td>
                    <td className="py-3 px-4"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Graf rozpracovanosti podle forecastu */}
        {pipelineData.length > 0 && isWidgetVisible('pipelineGraph') && (
          <div 
            className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8 relative ${
              layoutEditMode ? 'cursor-move hover:shadow-2xl transition-shadow' : ''
            } ${draggedOver === 'pipelineGraph' ? 'ring-4 ring-blue-400' : ''}`}
            draggable={layoutEditMode}
            onDragStart={(e) => handleDragStart(e, 'pipelineGraph')}
            onDragOver={(e) => handleDragOver(e, 'pipelineGraph')}
            onDragEnd={handleDragEnd}
            onDrop={(e) => handleDrop(e, 'pipelineGraph')}
          >
            <div className="absolute top-4 right-4">
              <FilterButton filterKey="pipelineGraph" containerName="Rozpracovanost podle forecastu" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
              Rozpracovanost podle forecastu
            </h2>
            <div className="space-y-4">
              {pipelineData.map((user, index) => {
                const maxTotal = pipelineData[0].total;
                
                return (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {user.name}
                      </span>
                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                        {user.total.toLocaleString('cs-CZ')} Kč
                      </span>
                    </div>
                    
                    <div className="relative w-full h-10 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden flex">
                      {/* Forecast 25% - Červená */}
                      {user.forecast25.bmsl > 0 && (
                        <div 
                          className="bg-red-500 dark:bg-red-600 flex items-center justify-center relative group cursor-pointer transition-all hover:opacity-80"
                          style={{ width: `${(user.forecast25.bmsl / maxTotal) * 100}%` }}
                          onClick={() => setPipelineModal({ userName: user.name, forecast: 25, companies: user.forecast25.companies })}
                        >
                          {(user.forecast25.bmsl / maxTotal) * 100 > 8 && (
                            <span className="text-xs font-semibold text-white px-1">
                              {user.forecast25.count} ({user.forecast25.bmsl.toLocaleString('cs-CZ', { maximumFractionDigits: 0 })} Kč)
                            </span>
                          )}
                          <div className="absolute hidden group-hover:block bg-gray-900 text-white text-xs rounded py-1 px-2 -top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap z-10 pointer-events-none">
                            Forecast 25%: {user.forecast25.count} firem, {user.forecast25.bmsl.toLocaleString('cs-CZ')} Kč
                          </div>
                        </div>
                      )}
                      
                      {/* Forecast 50% - Oranžová */}
                      {user.forecast50.bmsl > 0 && (
                        <div 
                          className="bg-orange-500 dark:bg-orange-600 flex items-center justify-center relative group cursor-pointer transition-all hover:opacity-80"
                          style={{ width: `${(user.forecast50.bmsl / maxTotal) * 100}%` }}
                          onClick={() => setPipelineModal({ userName: user.name, forecast: 50, companies: user.forecast50.companies })}
                        >
                          {(user.forecast50.bmsl / maxTotal) * 100 > 8 && (
                            <span className="text-xs font-semibold text-white px-1">
                              {user.forecast50.count} ({user.forecast50.bmsl.toLocaleString('cs-CZ', { maximumFractionDigits: 0 })} Kč)
                            </span>
                          )}
                          <div className="absolute hidden group-hover:block bg-gray-900 text-white text-xs rounded py-1 px-2 -top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap z-10 pointer-events-none">
                            Forecast 50%: {user.forecast50.count} firem, {user.forecast50.bmsl.toLocaleString('cs-CZ')} Kč
                          </div>
                        </div>
                      )}
                      
                      {/* Forecast 75% - Žlutá */}
                      {user.forecast75.bmsl > 0 && (
                        <div 
                          className="bg-yellow-400 dark:bg-yellow-500 flex items-center justify-center relative group cursor-pointer transition-all hover:opacity-80"
                          style={{ width: `${(user.forecast75.bmsl / maxTotal) * 100}%` }}
                          onClick={() => setPipelineModal({ userName: user.name, forecast: 75, companies: user.forecast75.companies })}
                        >
                          {(user.forecast75.bmsl / maxTotal) * 100 > 8 && (
                            <span className="text-xs font-semibold text-gray-800 px-1">
                              {user.forecast75.count} ({user.forecast75.bmsl.toLocaleString('cs-CZ', { maximumFractionDigits: 0 })} Kč)
                            </span>
                          )}
                          <div className="absolute hidden group-hover:block bg-gray-900 text-white text-xs rounded py-1 px-2 -top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap z-10 pointer-events-none">
                            Forecast 75%: {user.forecast75.count} firem, {user.forecast75.bmsl.toLocaleString('cs-CZ')} Kč
                          </div>
                        </div>
                      )}
                      
                      {/* Forecast 99% - Žluto-zelená */}
                      {user.forecast99.bmsl > 0 && (
                        <div 
                          className="bg-lime-500 dark:bg-lime-600 flex items-center justify-center relative group cursor-pointer transition-all hover:opacity-80"
                          style={{ width: `${(user.forecast99.bmsl / maxTotal) * 100}%` }}
                          onClick={() => setPipelineModal({ userName: user.name, forecast: 99, companies: user.forecast99.companies })}
                        >
                          {(user.forecast99.bmsl / maxTotal) * 100 > 8 && (
                            <span className="text-xs font-semibold text-gray-900 px-1">
                              {user.forecast99.count} ({user.forecast99.bmsl.toLocaleString('cs-CZ', { maximumFractionDigits: 0 })} Kč)
                            </span>
                          )}
                          <div className="absolute hidden group-hover:block bg-gray-900 text-white text-xs rounded py-1 px-2 -top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap z-10 pointer-events-none">
                            Forecast 99%: {user.forecast99.count} firem, {user.forecast99.bmsl.toLocaleString('cs-CZ')} Kč
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Legenda pod barem */}
                    <div className="flex gap-4 text-xs text-gray-600 dark:text-gray-400 justify-end">
                      {user.forecast25.count > 0 && (
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 bg-red-500 dark:bg-red-600 rounded"></span>
                          25%
                        </span>
                      )}
                      {user.forecast50.count > 0 && (
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 bg-orange-500 dark:bg-orange-600 rounded"></span>
                          50%
                        </span>
                      )}
                      {user.forecast75.count > 0 && (
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 bg-yellow-400 dark:bg-yellow-500 rounded"></span>
                          75%
                        </span>
                      )}
                      {user.forecast99.count > 0 && (
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 bg-lime-500 dark:bg-lime-600 rounded"></span>
                          99%
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Graf aktivit za poslední 2 týdny - denní rozpad */}
        {activitiesData.users && activitiesData.users.length > 0 && isWidgetVisible('activitiesGraph') && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8 relative">
            <div className="absolute top-4 right-4">
              <FilterButton filterKey="activitiesGraph" containerName="Aktivity za poslední 2 týdny" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
              Aktivity za poslední 2 týdny - denní rozpad
            </h2>
            
            {/* Tabulka s denním rozpadem */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-300 dark:border-gray-600">
                    <th className="text-left py-2 px-2 font-semibold text-gray-700 dark:text-gray-300 sticky left-0 bg-white dark:bg-gray-800 z-10">
                      Obchodník
                    </th>
                    {activitiesData.days.map((day, idx) => (
                      <th key={idx} className="text-center py-2 px-1 font-semibold text-gray-700 dark:text-gray-300 text-xs">
                        {day.label}
                      </th>
                    ))}
                    <th className="text-center py-2 px-3 font-semibold text-gray-700 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/30 min-w-[200px]">
                      Celkem (14 dní)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {activitiesData.users.map((user, userIdx) => {
                    const maxTotal = activitiesData.users[0].total;
                    const percentage = (user.total / maxTotal) * 100;
                    
                    return (
                      <tr key={userIdx} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <td className="py-2 px-2 font-medium text-gray-900 dark:text-white sticky left-0 bg-white dark:bg-gray-800">
                          {user.name}
                        </td>
                        {user.dailyCounts.map((count, dayIdx) => (
                          <td key={dayIdx} className="text-center py-2 px-1">
                            {count > 0 ? (
                              <span className={`inline-block min-w-[24px] px-1.5 py-0.5 rounded text-xs font-semibold ${
                                count >= 5 ? 'bg-green-500 text-white' :
                                count >= 3 ? 'bg-blue-500 text-white' :
                                count >= 1 ? 'bg-gray-400 text-white' :
                                'text-gray-400'
                              }`}>
                                {count}
                              </span>
                            ) : (
                              <span className="text-gray-300 dark:text-gray-600">-</span>
                            )}
                          </td>
                        ))}
                        <td className="py-2 px-3 bg-blue-50 dark:bg-blue-900/30">
                          <div className="flex items-center gap-3">
                            <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-6 overflow-hidden">
                              <div 
                                className="bg-gradient-to-r from-blue-500 to-blue-600 h-6 rounded-full flex items-center justify-end pr-2 transition-all duration-300"
                                style={{ width: `${percentage}%` }}
                              >
                                {percentage > 20 && (
                                  <span className="text-white text-xs font-bold">
                                    {user.total}
                                  </span>
                                )}
                              </div>
                            </div>
                            {percentage <= 20 && (
                              <span className="text-sm font-bold text-gray-900 dark:text-white w-8 text-right">
                                {user.total}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Graf plánovaných aktivit na následující 2 týdny */}
        {plannedActivitiesData.users && plannedActivitiesData.users.length > 0 && isWidgetVisible('plannedActivitiesGraph') && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8 relative">
            <div className="absolute top-4 right-4">
              <FilterButton filterKey="plannedActivitiesGraph" containerName="Plánované aktivity na následující 2 týdny" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
              📅 Plánované aktivity na následující 2 týdny
            </h2>
            
            {/* Tabulka s denním rozpadem */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-300 dark:border-gray-600">
                    <th className="text-left py-2 px-2 font-semibold text-gray-700 dark:text-gray-300 sticky left-0 bg-white dark:bg-gray-800 z-10">
                      Obchodník
                    </th>
                    {plannedActivitiesData.days.map((day, idx) => (
                      <th key={idx} className="text-center py-2 px-1 font-semibold text-gray-700 dark:text-gray-300 text-xs">
                        {day.label}
                      </th>
                    ))}
                    <th className="text-center py-2 px-3 font-semibold text-gray-700 dark:text-gray-300 bg-purple-50 dark:bg-purple-900/30 min-w-[200px]">
                      Celkem (14 dní)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {plannedActivitiesData.users.map((user, userIdx) => {
                    const maxTotal = plannedActivitiesData.users[0].total;
                    const percentage = (user.total / maxTotal) * 100;
                    
                    return (
                      <tr key={userIdx} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <td className="py-2 px-2 font-medium text-gray-900 dark:text-white sticky left-0 bg-white dark:bg-gray-800">
                          {user.name}
                        </td>
                        {user.dailyCounts.map((count, dayIdx) => (
                          <td key={dayIdx} className="text-center py-2 px-1">
                            {count > 0 ? (
                              <span className={`inline-block min-w-[24px] px-1.5 py-0.5 rounded text-xs font-semibold ${
                                count >= 5 ? 'bg-purple-500 text-white' :
                                count >= 3 ? 'bg-indigo-500 text-white' :
                                count >= 1 ? 'bg-gray-400 text-white' :
                                'text-gray-400'
                              }`}>
                                {count}
                              </span>
                            ) : (
                              <span className="text-gray-300 dark:text-gray-600">-</span>
                            )}
                          </td>
                        ))}
                        <td className="py-2 px-3 bg-purple-50 dark:bg-purple-900/30">
                          <div className="flex items-center gap-3">
                            <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-6 overflow-hidden">
                              <div 
                                className="bg-gradient-to-r from-purple-500 to-purple-600 h-6 rounded-full flex items-center justify-end pr-2 transition-all duration-300"
                                style={{ width: `${percentage}%` }}
                              >
                                {percentage > 20 && (
                                  <span className="text-white text-xs font-bold">
                                    {user.total}
                                  </span>
                                )}
                              </div>
                            </div>
                            {percentage <= 20 && (
                              <span className="text-sm font-bold text-gray-900 dark:text-white w-8 text-right">
                                {user.total}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* NOVÁ SEKCE: Končící smlouvy v následujících 24 měsících */}
        {contractExpirationsData.users && contractExpirationsData.users.length > 0 && (
          <div 
            className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8 ${
              layoutEditMode ? 'cursor-move hover:shadow-2xl transition-shadow' : ''
            } ${draggedOver === 'contractExpirations' ? 'ring-4 ring-blue-400' : ''}`}
            draggable={layoutEditMode}
            onDragStart={(e) => handleDragStart(e, 'contractExpirations')}
            onDragOver={(e) => handleDragOver(e, 'contractExpirations')}
            onDragEnd={handleDragEnd}
            onDrop={(e) => handleDrop(e, 'contractExpirations')}
          >
            <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
              📅 Končící smlouvy (RS) v následujících 24 měsících
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-300 dark:border-gray-600">
                    <th className="text-left py-2 px-2 font-semibold text-gray-700 dark:text-gray-300 sticky left-0 bg-white dark:bg-gray-800 z-10 min-w-[120px]">
                      Obchodník
                    </th>
                    {contractExpirationsData.months.map((month, idx) => (
                      <th key={idx} className="text-center py-2 px-1 font-semibold text-gray-700 dark:text-gray-300 text-xs min-w-[60px]">
                        {month.label}
                      </th>
                    ))}
                    <th className="text-center py-2 px-3 font-semibold text-gray-700 dark:text-gray-300 bg-orange-50 dark:bg-orange-900/30 min-w-[80px]">
                      Celkem
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {contractExpirationsData.users.map((user, userIdx) => (
                    <tr key={userIdx} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="py-2 px-2 font-medium text-gray-900 dark:text-white sticky left-0 bg-white dark:bg-gray-800">
                        {user.name}
                      </td>
                      {user.months.map((month, monthIdx) => (
                        <td key={monthIdx} className="text-center py-2 px-1">
                          {month.count > 0 ? (
                            <button
                              onClick={() => setExpirationModal({ 
                                userName: user.name, 
                                monthLabel: month.fullLabel, 
                                companies: month.companies 
                              })}
                              className={`inline-block min-w-[32px] px-2 py-1 rounded text-xs font-semibold cursor-pointer transition-all hover:scale-110 ${
                                month.count >= 10 ? 'bg-red-600 text-white' :
                                month.count >= 5 ? 'bg-orange-500 text-white' :
                                month.count >= 3 ? 'bg-yellow-500 text-gray-900' :
                                'bg-blue-500 text-white'
                              }`}
                              title={`${month.count} ${month.count === 1 ? 'firma' : month.count < 5 ? 'firmy' : 'firem'} - klikněte pro detail`}
                            >
                              {month.count}
                            </button>
                          ) : (
                            <span className="text-gray-300 dark:text-gray-600">-</span>
                          )}
                        </td>
                      ))}
                      <td className="text-center py-2 px-3 bg-orange-50 dark:bg-orange-900/30">
                        <span className="font-bold text-gray-900 dark:text-white">
                          {user.total}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Legenda */}
            <div className="flex gap-4 text-xs text-gray-600 dark:text-gray-400 mt-4 justify-end flex-wrap">
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 bg-blue-500 rounded"></span>
                1-2 firmy
              </span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 bg-yellow-500 rounded"></span>
                3-4 firmy
              </span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 bg-orange-500 rounded"></span>
                5-9 firem
              </span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 bg-red-600 rounded"></span>
                10+ firem
              </span>
            </div>
          </div>
        )}

        {/* Widget: Jak dlouho jsem pracoval v CRM? */}
        <WorkTimeWidget
          currentUserId={currentUserId}
          userRole={userRole}
          isDarkMode={isDarkMode}
          showAllUsersData={userRole === 'admin' && (widgetSettings.workTimeStats?.showAllUsersData ?? false)}
          onOpenSettings={() => setSettingsModal({ containerKey: 'workTimeStats', containerName: 'Jak dlouho jsem pracoval v CRM?' })}
        />

        {/* Modal pro nastavení viditelnosti */}
        {settingsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSettingsModal(null)}>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Nastavení widgetu
                  </h2>
                  <button
                    onClick={() => setSettingsModal(null)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {settingsModal.containerName}
                </p>
              </div>
              
              <div className="p-6 space-y-6">
                {/* 1. Viditelnost widgetu */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Viditelnost widgetu
                  </h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setWidgetSettings(prev => ({
                          ...prev,
                          [settingsModal.containerKey]: {
                            ...prev[settingsModal.containerKey],
                            visibleFor: 'all'
                          }
                        }));
                      }}
                      className={`w-full p-4 rounded-lg border-2 transition-all ${
                        widgetSettings[settingsModal.containerKey]?.visibleFor === 'all'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-left">
                          <p className="font-medium text-gray-900 dark:text-white">👥 Všichni uživatelé</p>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Widget vidí admin i běžní uživatelé</p>
                        </div>
                        {widgetSettings[settingsModal.containerKey]?.visibleFor === 'all' && (
                          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )}
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setWidgetSettings(prev => ({
                          ...prev,
                          [settingsModal.containerKey]: {
                            ...prev[settingsModal.containerKey],
                            visibleFor: 'admin'
                          }
                        }));
                      }}
                      className={`w-full p-4 rounded-lg border-2 transition-all ${
                        widgetSettings[settingsModal.containerKey]?.visibleFor === 'admin'
                          ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-orange-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-left">
                          <p className="font-medium text-gray-900 dark:text-white">🔒 Pouze Admin</p>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Widget vidí pouze administrátoři</p>
                        </div>
                        {widgetSettings[settingsModal.containerKey]?.visibleFor === 'admin' && (
                          <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )}
                      </div>
                    </button>
                  </div>
                </div>

                {/* 2. Zobrazení dat (uživatelé vs všichni) */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Zobrazení dat (admin vždy vidí vše)
                  </h3>
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {widgetSettings[settingsModal.containerKey]?.showAllUsersData ? 'Všichni uživatelé' : 'Pouze vlastní data'}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {widgetSettings[settingsModal.containerKey]?.showAllUsersData 
                          ? 'Uživatelé vidí data všech obchodníků'
                          : 'Uživatelé vidí pouze svá data'}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setWidgetSettings(prev => ({
                          ...prev,
                          [settingsModal.containerKey]: {
                            ...prev[settingsModal.containerKey],
                            showAllUsersData: !prev[settingsModal.containerKey]?.showAllUsersData
                          }
                        }));
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        widgetSettings[settingsModal.containerKey]?.showAllUsersData ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          widgetSettings[settingsModal.containerKey]?.showAllUsersData ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
                <button
                  onClick={() => setSettingsModal(null)}
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
                >
                  Uložit a zavřít
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Modal pro zobchodované firmy */}
        {completedModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setCompletedModal(null)}>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Zobchodováno tento měsíc
                  </h2>
                  <button
                    onClick={() => setCompletedModal(null)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Celkem {completedModal.companies.length} {completedModal.companies.length === 1 ? 'firma' : completedModal.companies.length < 5 ? 'firmy' : 'firem'}, 
                  {' '}{completedModal.companies.reduce((sum, c) => sum + (parseFloat(c.bmsl) || 0), 0).toLocaleString('cs-CZ')} Kč
                </p>
              </div>
              
              <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-6">
                <div className="space-y-3">
                  {completedModal.companies
                    .sort((a, b) => (parseFloat(b.bmsl) || 0) - (parseFloat(a.bmsl) || 0))
                    .map((company, idx) => (
                      <div 
                        key={company._id || idx}
                        className={`bg-gray-50 dark:bg-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors ${
                          userRole === 'admin' ? 'cursor-pointer' : 'cursor-default'
                        }`}
                        onClick={userRole === 'admin' ? () => {
                          window.open(window.location.href.replace(/#.*$/, '') + '#/companies/' + company._id, '_blank');
                        } : undefined}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                              {company.name}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              IČO: {company.ico} {company.address && `• ${company.address}`}
                            </p>
                            {company.contacts && company.contacts[0] && (
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {company.contacts[0].name && `${company.contacts[0].name} • `}
                                {company.contacts[0].phone && `📞 ${company.contacts[0].phone}`}
                                {company.contacts[0].email && ` • ✉️ ${company.contacts[0].email}`}
                              </p>
                            )}
                          </div>
                          <div className="text-right ml-4">
                            <div className="text-xl font-bold text-green-600 dark:text-green-400">
                              {parseFloat(company.bmsl).toLocaleString('cs-CZ')} Kč
                            </div>
                            <div className="text-xs font-semibold px-2 py-1 rounded mt-1 inline-block bg-green-500 text-white">
                              Forecast 100%
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Modal pro firmy před podpisem */}
        {beforeSignatureModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setBeforeSignatureModal(null)}>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Před podpisem (Forecast 75-99%)
                  </h2>
                  <button
                    onClick={() => setBeforeSignatureModal(null)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Celkem {beforeSignatureModal.companies.length} {beforeSignatureModal.companies.length === 1 ? 'firma' : beforeSignatureModal.companies.length < 5 ? 'firmy' : 'firem'}, 
                  {' '}{beforeSignatureModal.companies.reduce((sum, c) => sum + (parseFloat(c.bmsl) || 0), 0).toLocaleString('cs-CZ')} Kč
                </p>
              </div>
              
              <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-6">
                <div className="space-y-3">
                  {beforeSignatureModal.companies
                    .sort((a, b) => (parseFloat(b.bmsl) || 0) - (parseFloat(a.bmsl) || 0))
                    .map((company, idx) => (
                      <div 
                        key={company._id || idx}
                        className={`bg-gray-50 dark:bg-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors ${
                          userRole === 'admin' ? 'cursor-pointer' : 'cursor-default'
                        }`}
                        onClick={userRole === 'admin' ? () => {
                          window.open(window.location.href.replace(/#.*$/, '') + '#/companies/' + company._id, '_blank');
                        } : undefined}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                              {company.name}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              IČO: {company.ico} {company.address && `• ${company.address}`}
                            </p>
                            {company.contacts && company.contacts[0] && (
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {company.contacts[0].name && `${company.contacts[0].name} • `}
                                {company.contacts[0].phone && `📞 ${company.contacts[0].phone}`}
                                {company.contacts[0].email && ` • ✉️ ${company.contacts[0].email}`}
                              </p>
                            )}
                          </div>
                          <div className="text-right ml-4">
                            <div className="text-xl font-bold text-orange-600 dark:text-orange-400">
                              {parseFloat(company.bmsl).toLocaleString('cs-CZ')} Kč
                            </div>
                            <div className={`text-xs font-semibold px-2 py-1 rounded mt-1 inline-block ${
                              parseInt(company.forecast) === 75 ? 'bg-orange-400 text-white' :
                              parseInt(company.forecast) === 99 ? 'bg-green-500 text-white' :
                              'bg-yellow-400 text-gray-800'
                            }`}>
                              Forecast {company.forecast}%
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Modal: Rozpad poměru PO/FO podle obchodníků */}
        {showPoFoBreakdown && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={() => setShowPoFoBreakdown(false)}>
            <div className={`w-full max-w-2xl rounded-xl shadow-2xl ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} flex flex-col max-h-[90vh]`} onClick={e => e.stopPropagation()}>
              <div className={`flex items-center justify-between px-6 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <h2 className="text-lg font-bold">Poměr PO / FO podle obchodníků</h2>
                <button onClick={() => setShowPoFoBreakdown(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="overflow-y-auto p-6 space-y-5">
                {poFoByUser.length === 0 ? (
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Žádná data PO/FO.</p>
                ) : poFoByUser.map(user => (
                  <div key={user.name}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold">{user.name}</span>
                      <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        PO: {user.po} &nbsp;|&nbsp; FO: {user.fo} &nbsp;|&nbsp; celkem: {user.total}
                      </span>
                    </div>
                    <div className="flex w-full h-5 rounded overflow-hidden text-xs font-semibold">
                      {user.po > 0 && (
                        <div
                          className="flex items-center justify-center bg-purple-600 text-white"
                          style={{ width: `${user.poPct}%` }}
                          title={`PO: ${user.po} (${user.poPct}%)`}
                        >
                          {user.poPct > 12 && `${user.poPct}%`}
                        </div>
                      )}
                      {user.fo > 0 && (
                        <div
                          className="flex items-center justify-center bg-purple-300 text-purple-900"
                          style={{ width: `${user.foPct}%` }}
                          title={`FO: ${user.fo} (${user.foPct}%)`}
                        >
                          {user.foPct > 12 && `${user.foPct}%`}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-3 mt-1 text-xs">
                      <span className="flex items-center gap-1 text-purple-700 dark:text-purple-400">
                        <span className="w-2.5 h-2.5 rounded-full bg-purple-600 inline-block"></span>PO {user.poPct}%
                      </span>
                      <span className="flex items-center gap-1 text-purple-500 dark:text-purple-300">
                        <span className="w-2.5 h-2.5 rounded-full bg-purple-300 inline-block"></span>FO {user.foPct}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Modal: Rozpad poměru operátorů podle obchodníků */}
        {showOperatorBreakdown && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={() => setShowOperatorBreakdown(false)}>
            <div className={`w-full max-w-2xl rounded-xl shadow-2xl ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} flex flex-col max-h-[90vh]`} onClick={e => e.stopPropagation()}>
              <div className={`flex items-center justify-between px-6 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <h2 className="text-lg font-bold">Poměr operátorů podle obchodníků</h2>
                <button onClick={() => setShowOperatorBreakdown(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="overflow-y-auto p-6 space-y-5">
                {operatorByUser.length === 0 ? (
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Žádná data operátorů.</p>
                ) : operatorByUser.map(user => (
                  <div key={user.name}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold">{user.name}</span>
                      <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        O2: {user.o2} &nbsp;|&nbsp; T-Mobile: {user.tmobile} &nbsp;|&nbsp; celkem: {user.total}
                      </span>
                    </div>
                    <div className="flex w-full h-5 rounded overflow-hidden text-xs font-semibold">
                      {user.o2 > 0 && (
                        <div
                          className="flex items-center justify-center bg-cyan-500 text-white"
                          style={{ width: `${user.o2Pct}%` }}
                          title={`O2: ${user.o2} (${user.o2Pct}%)`}
                        >
                          {user.o2Pct > 12 && `${user.o2Pct}%`}
                        </div>
                      )}
                      {user.tmobile > 0 && (
                        <div
                          className="flex items-center justify-center bg-pink-600 text-white"
                          style={{ width: `${user.tmobilePct}%` }}
                          title={`T-Mobile: ${user.tmobile} (${user.tmobilePct}%)`}
                        >
                          {user.tmobilePct > 12 && `${user.tmobilePct}%`}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-3 mt-1 text-xs">
                      <span className="flex items-center gap-1 text-cyan-600 dark:text-cyan-400">
                        <span className="w-2.5 h-2.5 rounded-full bg-cyan-500 inline-block"></span>O2 {user.o2Pct}%
                      </span>
                      <span className="flex items-center gap-1 text-pink-600 dark:text-pink-400">
                        <span className="w-2.5 h-2.5 rounded-full bg-pink-600 inline-block"></span>T-Mobile {user.tmobilePct}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Modal pro detail rozpracovanosti */}
        {pipelineModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setPipelineModal(null)}>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {pipelineModal.userName} - Forecast {pipelineModal.forecast}%
                  </h2>
                  <button
                    onClick={() => setPipelineModal(null)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Celkem {pipelineModal.companies.length} {pipelineModal.companies.length === 1 ? 'firma' : pipelineModal.companies.length < 5 ? 'firmy' : 'firem'}, 
                  {' '}{pipelineModal.companies.reduce((sum, c) => sum + (parseFloat(c.bmsl) || 0), 0).toLocaleString('cs-CZ')} Kč
                </p>
              </div>
              
              <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-6">
                <div className="space-y-3">
                  {pipelineModal.companies
                    .sort((a, b) => (parseFloat(b.bmsl) || 0) - (parseFloat(a.bmsl) || 0))
                    .map((company, idx) => {
                      const lastActivity = (company.activities || [])
                        .slice()
                        .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0))
                        .slice(-1)[0];
                      const isEditing = editingForecast?.companyId === company._id;
                      const canEditForecast = userRole === 'admin' || company.createdBy === currentUserId || company.createdBy?._id === currentUserId;
                      
                      return (
                        <div 
                          key={company._id || idx}
                          className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        >
                          <div className="flex justify-between items-start gap-4">
                            <div 
                              className={`flex-1 ${
                                userRole === 'admin' ? 'cursor-pointer' : 'cursor-default'
                              }`}
                              onClick={userRole === 'admin' ? () => {
                                window.open(window.location.href.replace(/#.*$/, '') + '#/companies/' + company._id, '_blank');
                              } : undefined}
                            >
                              <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                                {company.name}
                              </h3>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                IČO: {company.ico}
                              </p>
                              
                              <div className="mt-2 space-y-1">
                                {company.nextActionDate && (
                                  <p className="text-sm text-gray-600 dark:text-gray-400">
                                    📅 Další akce: {new Date(company.nextActionDate).toLocaleDateString('cs-CZ')} {new Date(company.nextActionDate).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}
                                  </p>
                                )}
                                {lastActivity && (
                                  <p className="text-sm text-gray-600 dark:text-gray-400">
                                    💬 Aktivita: {lastActivity.text}
                                  </p>
                                )}
                                {company.status && (
                                  <p className="text-sm">
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                      {company.status}
                                    </span>
                                  </p>
                                )}
                              </div>
                            </div>
                            
                            <div className="text-right flex-shrink-0">
                              <div className="text-xl font-bold text-gray-900 dark:text-white">
                                {parseFloat(company.bmsl).toLocaleString('cs-CZ')} Kč
                              </div>
                              
                              <div className="mt-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                {canEditForecast ? (
                                  <>
                                    {isEditing ? (
                                      <>
                                        <select
                                          value={editingForecast.newForecast}
                                          onChange={(e) => setEditingForecast({ ...editingForecast, newForecast: e.target.value })}
                                          className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                        >
                                          <option value="0">0%</option>
                                          <option value="25">25%</option>
                                          <option value="50">50%</option>
                                          <option value="75">75%</option>
                                          <option value="99">99%</option>
                                        </select>
                                        <button
                                          onClick={async () => {
                                            try {
                                              const res = await fetch(`${API_URL}/companies/${company._id}`, {
                                                method: 'PUT',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ forecast: editingForecast.newForecast })
                                              });
                                              if (res.ok) {
                                                // Aktualizuj data v modalu
                                                const updatedCompanies = pipelineModal.companies.map(c => 
                                                  c._id === company._id ? { ...c, forecast: editingForecast.newForecast } : c
                                                );
                                                setPipelineModal({ ...pipelineModal, companies: updatedCompanies });
                                                setEditingForecast(null);
                                                // Aktualizuj globální data
                                                await fetchCompanies(currentUserId);
                                              }
                                            } catch (err) {
                                              console.error('Error updating forecast:', err);
                                            }
                                          }}
                                          className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded"
                                        >
                                          ✓
                                        </button>
                                        <button
                                          onClick={() => setEditingForecast(null)}
                                          className="px-2 py-1 bg-gray-400 hover:bg-gray-500 text-white text-xs rounded"
                                        >
                                          ✕
                                        </button>
                                      </>
                                    ) : (
                                      <button
                                        onClick={() => setEditingForecast({ companyId: company._id, newForecast: company.forecast || '0' })}
                                        className={`text-xs font-semibold px-2 py-1 rounded cursor-pointer hover:opacity-80 ${
                                          company.forecast === 25 || company.forecast === '25' ? 'bg-red-500 text-white' :
                                          company.forecast === 50 || company.forecast === '50' ? 'bg-orange-500 text-white' :
                                          company.forecast === 75 || company.forecast === '75' ? 'bg-yellow-400 text-gray-800' :
                                          company.forecast === 99 || company.forecast === '99' ? 'bg-lime-500 text-gray-900' :
                                          'bg-gray-400 text-white'
                                        }`}
                                      >
                                        Forecast {company.forecast || 0}%
                                      </button>
                                    )}
                                  </>
                                ) : (
                                  <div className={`text-xs font-semibold px-2 py-1 rounded ${
                                    company.forecast === 25 || company.forecast === '25' ? 'bg-red-500 text-white' :
                                    company.forecast === 50 || company.forecast === '50' ? 'bg-orange-500 text-white' :
                                    company.forecast === 75 || company.forecast === '75' ? 'bg-yellow-400 text-gray-800' :
                                    company.forecast === 99 || company.forecast === '99' ? 'bg-lime-500 text-gray-900' :
                                    'bg-gray-400 text-white'
                                  }`}>
                                    Forecast {company.forecast || 0}%
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal pro končící smlouvy */}
        {expirationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setExpirationModal(null)}>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    📅 {expirationModal.userName} - Končící smlouvy
                  </h2>
                  <button
                    onClick={() => setExpirationModal(null)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {expirationModal.monthLabel} • Celkem {expirationModal.companies.length} {
                    expirationModal.companies.length === 1 ? 'firma' : 
                    expirationModal.companies.length < 5 ? 'firmy' : 'firem'
                  }
                </p>
              </div>
              
              <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-6">
                <div className="space-y-3">
                  {expirationModal.companies
                    .sort((a, b) => new Date(a.rsEndDate) - new Date(b.rsEndDate))
                    .map((company, idx) => (
                      <div 
                        key={company._id || idx}
                        className={`bg-gray-50 dark:bg-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors ${
                          userRole === 'admin' ? 'cursor-pointer' : 'cursor-default'
                        }`}
                        onClick={userRole === 'admin' ? () => {
                          window.open(window.location.href.replace(/#.*$/, '') + '#/companies/' + company._id, '_blank');
                        } : undefined}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                              {company.name}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              IČO: {company.ico} {company.address && `• ${company.address}`}
                            </p>
                            {company.contacts && company.contacts[0] && (
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {company.contacts[0].name && `${company.contacts[0].name} • `}
                                {company.contacts[0].phone && `📞 ${company.contacts[0].phone}`}
                                {company.contacts[0].email && ` • ✉️ ${company.contacts[0].email}`}
                              </p>
                            )}
                            
                            {/* Poslední aktivita */}
                            <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded border-l-4 border-blue-500">
                              <p className="text-xs font-semibold text-blue-800 dark:text-blue-300 mb-1">
                                📝 Poslední aktivita:
                              </p>
                              <p className="text-sm text-gray-700 dark:text-gray-300">
                                {company.lastActivityText}
                              </p>
                              {company.lastActivityDate && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {new Date(company.lastActivityDate).toLocaleDateString('cs-CZ', {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="text-right ml-4">
                            <div className="text-lg font-bold text-red-600 dark:text-red-400">
                              Konec RS
                            </div>
                            <div className="text-xl font-semibold text-gray-900 dark:text-white mt-1">
                              {new Date(company.rsEndDate).toLocaleDateString('cs-CZ', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </div>
                            {company.operator && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                Operátor: {company.operator}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal pro podepsané obchody */}
        {signedDealsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSignedDealsModal(null)}>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    ✅ {signedDealsModal.userName} - Podepsané obchody
                  </h2>
                  <button
                    onClick={() => setSignedDealsModal(null)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {new Date().toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' })} • Celkem {signedDealsModal.companies.length} {
                    signedDealsModal.companies.length === 1 ? 'firma' : 
                    signedDealsModal.companies.length < 5 ? 'firmy' : 'firem'
                  }
                </p>
              </div>
              
              <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-6">
                <div className="space-y-3">
                  {signedDealsModal.companies
                    .sort((a, b) => new Date(b.closedDate || b.zobchodovano) - new Date(a.closedDate || a.zobchodovano))
                    .map((company, idx) => (
                      <div 
                        key={company._id || idx}
                        className={`bg-gray-50 dark:bg-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors ${
                          userRole === 'admin' ? 'cursor-pointer' : 'cursor-default'
                        }`}
                        onClick={userRole === 'admin' ? () => {
                          window.open(window.location.href.replace(/#.*$/, '') + '#/companies/' + company._id, '_blank');
                        } : undefined}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                              {company.name}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              IČO: {company.ico} {company.address && `• ${company.address}`}
                            </p>
                            {company.contacts && company.contacts[0] && (
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {company.contacts[0].name && `${company.contacts[0].name} • `}
                                {company.contacts[0].phone && `📞 ${company.contacts[0].phone}`}
                                {company.contacts[0].email && ` • ✉️ ${company.contacts[0].email}`}
                              </p>
                            )}
                            
                            {/* BMSL */}
                            {company.bmsl && (
                              <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 rounded border-l-4 border-green-500">
                                <p className="text-xs font-semibold text-green-800 dark:text-green-300 mb-1">
                                  💰 BMSL:
                                </p>
                                <p className="text-lg font-bold text-gray-900 dark:text-white">
                                  {parseFloat(company.bmsl).toLocaleString('cs-CZ')} Kč
                                </p>
                              </div>
                            )}
                          </div>
                          <div className="text-right ml-4">
                            <div className="text-sm font-medium text-green-600 dark:text-green-400">
                              Zobchodováno
                            </div>
                            <div className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                              {new Date(company.closedDate || company.zobchodovano).toLocaleDateString('cs-CZ', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                              Forecast: 100%
                            </div>
                            {company.operator && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Operátor: {company.operator}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
